import React, { useState, useEffect } from 'react'
import { ColumnDefinition } from 'common/components/DataTable'
import GenericList from 'common/components/GenericList'
import { Button, Form, Container, Row, Col } from 'react-bootstrap'
import * as Icon from 'react-bootstrap-icons'
import EditVariableWorkflow from 'application/variables/EditVariableWorkflow'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { getCachedTheme, setTheme, Theme } from 'common/logic/Theme'
import { Variable } from 'types'

function Settings() {
    const [modal, setModal] = useState<{ type: string; name?: string; variableType?: string } | false>(false)
    const [dataKey, setDataKey] = useState(Date.now())
    const [advancedExpanded, setAdvancedExpanded] = useState(false)
    const [isDarkMode, setIsDarkMode] = useState(getCachedTheme() === 'dark')
    const hideDialogs = () => setModal(false)
    const refreshData = () => setDataKey(Date.now())

    useEffect(() => {
        let cancelled = false
        API.getVariable('ui_theme', 'string')
            .then((response: any) => {
                if (cancelled) return
                const theme = response.data.value as Theme
                const isDark = theme === 'dark'
                setIsDarkMode(isDark)
                if (theme === 'light' || theme === 'dark') {
                    setTheme(theme)
                }
            })
            .catch(() => {
                if (cancelled) return
                if (getCachedTheme() === null) {
                    setTheme('light')
                }
            })
        return () => { cancelled = true }
    }, [])

    const handleDarkModeToggle = (checked: boolean) => {
        setIsDarkMode(checked)
        setTheme(checked ? 'dark' : 'light')

        const themeValue = checked ? 'dark' : 'light'
        API.setVariable('ui_theme', themeValue, 'string').catch(() => {
            // Error saving theme, but don't show to user - theme is still applied locally
        })
    }

    const showCreateDialog = (type: string) => setModal({ type: "create", variableType: type })
    const showEditDialog = (name: string) => setModal({ type: "edit", name, variableType: "string" })
    const showDeletionDialog = (name: string) => setModal({ type: "delete", name })

    const creationOrEditModal = (params: { name?: string; variableType?: string }) => {
        const completion = (result: unknown) => {
            hideDialogs()
            if (result) refreshData()
        }
        return <EditVariableWorkflow onSuccess={completion} variableName={params.name} variableType={(params.variableType || "string") as "string" | "int"} />
    }

    const deletionModal = (params: { name: string }) => {
        const confirmationStep = () => `Delete variable "${params.name}"?`
        const executeStep = () => API.delete(`/variables/${params.name}`)
        const completion = (result: unknown) => {
            hideDialogs()
            if (result) refreshData()
        }
        return <OperationWorkflowModal
            confirmationStep={confirmationStep}
            executionStep={executeStep}
            completionCallback={completion}
        />
    }

    let modalEl: React.ReactNode = null
    if (modal) {
        switch ((modal as any).type) {
            case "create":
            case "edit":
                modalEl = creationOrEditModal(modal as { name?: string; variableType?: string })
                break
            case "delete":
                modalEl = deletionModal(modal as { name: string })
                break
            default:
        }
    }

    const definitions = [
        new ColumnDefinition<Variable>("Name", "name"),
        new ColumnDefinition<Variable>("Value", "value"),
        new ColumnDefinition<Variable>("Actions", obj => {
            return <>
                <Button variant="link" title="Edit"
                    onClick={() => showEditDialog(obj.name)}><Icon.Pencil /></Button>
                <Button variant="link" title="Delete"
                    onClick={() => showDeletionDialog(obj.name)}><Icon.Trash /></Button>
            </>
        })
    ]

    return <>
        {modalEl}
        <div>
            <h2>Account Settings</h2>
            <Container fluid className="mt-4">
                <Row className="mb-4">
                    <Col md={6}>
                        <div className="settings-section">
                            <Form.Group className="d-flex align-items-center justify-content-between">
                                <Form.Label className="mb-0">Dark Mode</Form.Label>
                                <Form.Check
                                    type="switch"
                                    id="dark-mode-switch"
                                    checked={isDarkMode}
                                    onChange={(e) => handleDarkModeToggle(e.target.checked)}
                                />
                            </Form.Group>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setAdvancedExpanded(!advancedExpanded)}
                        >
                            {advancedExpanded ? <>
                                <Icon.ChevronUp /> Advanced...
                            </> : <>
                                <Icon.ChevronDown /> Advanced...
                            </>}
                        </Button>
                    </Col>
                </Row>

                {advancedExpanded && (
                    <Row className="mt-4">
                        <Col>
                            <GenericList
                                title="Variables"
                                columns={definitions as unknown as ColumnDefinition[]}
                                beforeTable={<>
                                    <Button variant="link" onClick={() => showCreateDialog("string")}>Add string variable</Button>
                                    <Button variant="link" onClick={() => showCreateDialog("int")}>Add int variable</Button>
                                </>}
                                dataKey={dataKey}
                                endpoint="/variables"
                            />
                        </Col>
                    </Row>
                )}
            </Container>
        </div>
    </>
}

export default Settings
